import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, apiOpcional, definirAoPerderSessao } from '../api/cliente';
import type { DadosCadastro, RespostaSessao, Usuario } from '../api/tipos';
import { origemDoCadastro } from '../util/visita';

interface Sessao {
  usuario: Usuario | null;
  /** `true` enquanto a sessão do cookie ainda não foi resolvida no boot. */
  carregando: boolean;
  /** Devolve o usuário autenticado: quem chama decide para onde navegar. */
  entrar: (email: string, senha: string) => Promise<Usuario>;
  cadastrar: (dados: DadosCadastro) => Promise<void>;
  sair: () => Promise<void>;
  /** Recarrega o perfil (após trocar dados da conta, por exemplo). */
  recarregar: () => Promise<void>;
}

const Contexto = createContext<Sessao | null>(null);

export function ProvedorSessao({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [carregando, setCarregando] = useState(true);

  /**
   * Descobre se já existe sessão perguntando ao servidor.
   *
   * Não dá para checar o cookie no JS (é `httpOnly`, e é esse o ponto): a única
   * fonte de verdade é `GET /auth/eu`. Também cobre o caso de a conta ter sido
   * desativada enquanto a aba estava aberta.
   */
  const recarregar = useCallback(async () => {
    const perfil = await apiOpcional<Usuario>('/auth/eu');
    setUsuario(perfil);
  }, []);

  useEffect(() => {
    void recarregar().finally(() => setCarregando(false));
  }, [recarregar]);

  // Quando o refresh falha de vez, o cliente avisa e a sessão cai aqui — sem
  // isso a tela ficaria mostrando dados de uma sessão que já morreu.
  useEffect(() => {
    definirAoPerderSessao(() => setUsuario(null));
    return () => definirAoPerderSessao(() => {});
  }, []);

  const entrar = useCallback(async (email: string, senha: string) => {
    const s = await api<RespostaSessao>('/auth/login', {
      metodo: 'POST',
      corpo: { email, senha },
    });
    setUsuario(s.usuario);
    // Devolvido em vez de lido do estado logo depois: `setUsuario` não é
    // síncrono, e a tela de login navegaria com o valor anterior (null).
    return s.usuario;
  }, []);

  const cadastrar = useCallback(async (dados: DadosCadastro) => {
    const s = await api<RespostaSessao>('/auth/registrar', {
      metodo: 'POST',
      corpo: { ...dados, ...origemDoCadastro() },
    });
    setUsuario(s.usuario);
  }, []);

  const sair = useCallback(async () => {
    try {
      await api<void>('/auth/logout', { metodo: 'POST', corpo: {} });
    } finally {
      // Mesmo se a chamada falhar (rede caiu), a UI tem que sair do estado
      // logado — os cookies expiram sozinhos e o servidor já revogou o que deu.
      setUsuario(null);
    }
  }, []);

  const valor = useMemo(
    () => ({ usuario, carregando, entrar, cadastrar, sair, recarregar }),
    [usuario, carregando, entrar, cadastrar, sair, recarregar],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useSessao(): Sessao {
  const ctx = useContext(Contexto);
  if (!ctx) {
    throw new Error('useSessao precisa estar dentro de <ProvedorSessao>.');
  }
  return ctx;
}
